import React from 'react';
import { useModalStack } from '../../../../components/Modal/ModalStackContext';
import { CalendarCoursePreview, CalendarSimulationPreview, CalendarCollectionPreview } from './CalendarModalWrappers';
import AddItemModal from './AddItemModal';
import EditItemModal from './EditItemModal';
import MultiItemSelectModal from './MultiItemSelectModal';
import ExportToPresetModal from './ExportToPresetModal';

const CalendarModalRenderer = ({
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    handleCollectionSelection,
    onAddNewItemFromMultiSelect,
    onMultiSelectItemClick,
    simulationData,
    courseData,
    collectionData,
    onAdd,
    onUpdate,
    onDelete,
    onEnterMoveMode,
    onPreview,
    contentTypeColors,
    calendarItems,
    showNotification,
    featureSwitchesApiData
}) => {
    const { modalStack, popModal } = useModalStack();

    // Wrapper functions that close modal after action
    const handleAddWithClose = () => {
        onAdd();
        popModal();
    };

    const handleUpdateWithClose = () => {
        onUpdate();
        popModal();
    };

    const handleDeleteWithClose = () => {
        onDelete();
        popModal();
    };

    const handleEnterMoveModeWithClose = () => {
        onEnterMoveMode();
        popModal();
    };

    const renderModal = (modal, index) => {
        const isTopModal = index === modalStack.length - 1;
        // Use larger z-index increments for proper layering
        const zIndex = 1300 + (index * 100);

        const commonProps = {
            key: modal.id,
            // FIXED: Only the top modal should be open, others should be closed/hidden
            open: isTopModal,
            style: { zIndex }
        };

        switch (modal.type) {
            case 'multiSelect':
                return (
                    <MultiItemSelectModal
                        {...commonProps}
                        items={modal.props.items}
                        date={modal.props.date}
                        readOnly={modal.props.readOnly}
                        onClose={popModal}
                        onAddNewItem={() => onAddNewItemFromMultiSelect(modal.props.date)}
                        onItemClick={onMultiSelectItemClick}
                        contentTypeColors={contentTypeColors}
                    />
                );

            case 'addItem':
                return (
                    <AddItemModal
                        {...commonProps}
                        date={modal.props.date}
                        onClose={popModal}
                        onAdd={handleAddWithClose} // Use wrapper that closes modal
                        onPreview={onPreview}
                        formState={formState}
                        handleFormChange={handleFormChange}
                        handleContentTypeChange={handleContentTypeChange}
                        handleContentSelection={handleContentSelection}
                        handleCollectionSelection={handleCollectionSelection}
                        simulationData={simulationData}
                        courseData={courseData}
                        collectionData={collectionData}
                        featureSwitchesApiData={featureSwitchesApiData} 
                    />
                );

            case 'editItem':
                return (
                    <EditItemModal
                        {...commonProps}
                        item={modal.props.item}
                        onClose={popModal}
                        onUpdate={handleUpdateWithClose} // Use wrapper that closes modal
                        onDelete={handleDeleteWithClose} // Use wrapper that closes modal
                        onEnterMoveMode={handleEnterMoveModeWithClose} // Use wrapper that closes modal
                        onPreview={onPreview}
                        formState={formState}
                        handleFormChange={handleFormChange}
                        handleContentTypeChange={handleContentTypeChange}
                        handleContentSelection={handleContentSelection}
                        handleCollectionSelection={handleCollectionSelection}
                        simulationData={simulationData}
                        courseData={courseData}
                        collectionData={collectionData}
                        featureSwitchesApiData={featureSwitchesApiData} 
                    />
                );

            case 'coursePreview':
                return (
                    <CalendarCoursePreview
                        {...commonProps}
                        courseId={modal.props.courseId}
                        onClose={popModal}
                    />
                );

            case 'simulationPreview':
                return (
                    <CalendarSimulationPreview
                        {...commonProps}
                        simulationId={modal.props.simulationId}
                        onClose={popModal}
                    />
                );

            case 'collectionPreview':
                return (
                    <CalendarCollectionPreview
                        {...commonProps}
                        collectionId={modal.props.collectionId}
                        onClose={popModal}
                    />
                );

            case 'exportPreset':
                return (
                    <ExportToPresetModal
                        {...commonProps}
                        onClose={popModal}
                        calendarItems={calendarItems}
                        showNotification={showNotification}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <>
            {modalStack.map((modal, index) => renderModal(modal, index))}
        </>
    );
};

export default CalendarModalRenderer;